<script setup lang="ts">
import { useMediaQuery, useWindowSize } from "@vueuse/core";
import { computed, onMounted, onUnmounted, watch, watchEffect } from "vue";
import { RouterView } from "vue-router";

import AppDownloadModal from "~/components/AppDownloadModal.vue";
import ChatPanel from "~/components/chat/ChatPanel.vue";
import BroadcastBanner from "~/components/dumb/BroadcastBanner.vue";
import DeleteConfirmationDialog from "~/components/dumb/DeleteConfirmationDialog.vue";
import DraggableDivider from "~/components/dumb/DraggableDivider.vue";
import Modal from "~/components/dumb/Modal.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import FeedbackModal from "~/components/FeedbackModal.vue";
import SearchModal from "~/components/filters/SearchModal/SearchModal.vue";
import InternalFormModal from "~/components/forms/InternalFormModal.vue";
import FullscreenMediaModal from "~/components/FullscreenMediaModal.vue";
import ImportTemplateModal from "~/components/ImportTemplateModal.vue";
import Leftbar from "~/components/leftbar/Leftbar.vue";
import LinkModal from "~/components/LinkModal.vue";
import MobileBottombar from "~/components/MobileBottombar.vue";
import MobileRefreshSwipe from "~/components/MobileRefreshSwipe.vue";
import NotionLinkModal from "~/components/NotionLinkModal.vue";
import OnboardingModal from "~/components/OnboardingModal.vue";
import PagePermissionsModal from "~/components/PagePermissionsModal.vue";
import PlanProjectModal from "~/components/PlanProjectModal.vue";
import RemapModal from "~/components/RemapModal.vue";
import ReportCreationModal from "~/components/ReportCreationModal.vue";
import ShortcutsModal from "~/components/ShortcutsModal.vue";
import SwitchToDesktopModal from "~/components/SwitchToDesktopModal.vue";
import TaskEditModal from "~/components/task/TaskEditModal.vue";
import Tcm from "~/components/task/Tcm.vue";
import Topbar from "~/components/topbar/Topbar.vue";
import TrialEndedModal from "~/components/TrialEndedModal.vue";
import TrialStartedModal from "~/components/TrialStartedModal.vue";
import { isLeftbarVisible } from "~/shared/common";
import { ModalPosition, ModalWidth } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { callDesktop } from "~/utils/desktop";
import { useLeftbarSwipe } from "~/utils/swipe";
import SettingsView from "~/views/SettingsView.vue";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const transparentBg = pageStore.isDesktopApp && pageStore.isMac && pageStore.isVersionGreaterOrEqual("1.0.2");
const isPrinting = useMediaQuery("print");

const showMobileBottombar = computed(
  () => pageStore.isMobile && !appStore.taskOpenInDetail && !appStore.chatPanelOpen && !appStore.docOpenInFullscreen
);

const shouldShowLeftbar = computed(() => {
  if (appStore.docOpenInFullscreen || appStore.taskOpenInDetail || appStore.isModalOpen) {
    return false;
  }
  return isLeftbarVisible(appStore.currentPage);
});

const leftbarSwipe = pageStore.isMobile
  ? useLeftbarSwipe(
      computed(() => appStore.mobileLeftbarOpen),
      computed(() => !shouldShowLeftbar.value),
      appStore.setMobileLeftbarOpen
    )
  : undefined;

onMounted(() => {
  if (!pageStore.isMobile || !leftbarSwipe) {
    return;
  }
  document.addEventListener("touchstart", leftbarSwipe.onTouchstart, { passive: false });
  document.addEventListener("touchmove", leftbarSwipe.onTouchmove, { passive: false });
  document.addEventListener("touchend", leftbarSwipe.onTouchend, { passive: false });
});

onUnmounted(() => {
  if (!pageStore.isMobile || !leftbarSwipe) {
    return;
  }
  document.removeEventListener("touchstart", leftbarSwipe.onTouchstart);
  document.removeEventListener("touchmove", leftbarSwipe.onTouchmove);
  document.removeEventListener("touchend", leftbarSwipe.onTouchend);
});

const onSaveLeftbarPaneSize = (width: number) => {
  userStore.setLeftbarWidthPx(width);
};

const onSaveChatPanelSize = (width: number) => {
  userStore.setChatPanelWidthPx(width);
};

const onChatPanelEdge = (left: boolean) => {
  if (left) {
    return;
  }
  appStore.setChatPanelOpen(false);
};

const close = () => {
  if (pageStore.isMobile) {
    return;
  }
  appStore.setMobileLeftbarOpen(false);
};

watchEffect(() => {
  appStore.leftbarInCollapsibleMode = useWindowSize().width.value < 1024;
});

/* Send notification count to desktop app */
watch(
  () => dataStore.unreadNotificationCount,
  (count) => {
    callDesktop(() => DESKTOP.setNotificationCount(count));
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="pageStore.isLoggedIn" class="relative flex size-full">
    <div class="flex size-full flex-col">
      <MobileRefreshSwipe v-if="pageStore.isMobile && !appStore.isModalOpen && !appStore.mobileLeftbarOpen" />

      <BroadcastBanner class="print:hidden" />
      <div class="flex size-full">
        <!-- Mobile sidebar -->
        <Modal
          id="mobile-leftbar"
          :entity="appStore.mobileLeftbarOpen"
          title="Leftbar"
          hide-title
          :width="ModalWidth.XS"
          :position="ModalPosition.LEFT"
          no-padding
          custom-styles="overflow-hidden h-full !px-1 !pt-1 !pb-0 print:hidden rounded-none"
          :panel-styles="leftbarSwipe?.swipingStyles?.value"
          close-icon-styles="hidden"
          overflow-clip
          mobile-no-swipe
         
          @close="close">
          <template #default>
            <Leftbar is-mobile-leftbar />
          </template>
        </Modal>
        <DraggableDivider
          :pane-width-px="userStore.leftbarWidthPx"
          :pane-min-px="192"
          :pane-max-px="400"
          left
          :hide-pane="isPrinting || appStore.mobileLeftbarOpen || appStore.leftbarInCollapsibleMode"
          styles="flex h-full grow flex-col"
          @save="onSaveLeftbarPaneSize">
          <template #pane>
            <!-- Desktop sidebar -->
            <div class="flex size-full flex-col border-r border-md">
              <div
               
                class="group/leftbar flex grow flex-col overflow-y-auto"
                :class="transparentBg ? 'bg-gray-100 dark:bg-zinc-800/80' : 'bg-lt'">
                <Leftbar />
              </div>
            </div>
          </template>

          <template #default>
            <!-- Topbar and main -->
            <Topbar />
            <div class="flex min-h-0 w-full grow">
              <DraggableDivider
                v-if="!(pageStore.isMobile && appStore.chatPanelOpen)"
                :hide-pane="!appStore.chatPanelOpen"
                :pane-width-px="userStore.chatPanelWidthPx"
                :pane-min-px="192"
                :content-min-px="672"
                styles="flex h-full grow"
                @save="onSaveChatPanelSize"
                @edge="onChatPanelEdge">
                <template #pane>
                  <ChatPanel />
                </template>

                <template #default>
                  <RouterView />
                </template>
              </DraggableDivider>
              <ChatPanel v-else />
            </div>
            <MobileBottombar v-if="showMobileBottombar" />
          </template>
        </DraggableDivider>

        <AppDownloadModal />
        <DeleteConfirmationDialog />
        <FeedbackModal />
        <FullscreenMediaModal />
        <ImportTemplateModal />
        <InternalFormModal />
        <LinkModal />
        <NotionLinkModal />
        <PagePermissionsModal />
        <PlanProjectModal />
        <RemapModal />
        <ReportCreationModal />
        <SearchModal />
        <SettingsView />
        <ShortcutsModal />
        <SwitchToDesktopModal />
        <TaskEditModal />
        <Tcm />
        <TrialEndedModal />
        <TrialStartedModal />
        <UpgradeConfirmationDialog is-global />

        <!-- Should be on top -->
        <OnboardingModal />
      </div>
    </div>
  </div>
</template>
